'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { createProductReview } from '@eliadmin/core/src/api/products/product-api';
import { applyServerErrors } from '@eliadmin/core/src/components/nt/account/shell/server-errors';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

import { EMPTY_REVIEW, reviewSchema, type ReviewValues } from './review-schema';
import type { WriteReviewTexts } from './write-review-texts';

/** The fields the write-review API validates – a 422 names them in `errors`. */
const isReviewField = (field: string): field is keyof ReviewValues =>
  field === 'rating' || field === 'review';

/**
 * Form of the „Napísať hodnotenie“ modal with the submit of the core `AddReviewDialog`: the review
 * goes to `POST /store/products/{slug}/write-review` with the customer token; a 422 lands on its
 * fields, any other failure is the error toast. A success toasts, calls `onSent` (closes the
 * modal), refreshes the server-rendered reviews and the cached review lists; `resetAfterSent`
 * then empties the form once the modal is gone (not while it fades out). The form lives above the
 * modal content, so a draft survives closing the modal without sending.
 */
export function useReviewForm({
  slug,
  texts,
  onSent,
}: {
  slug: string;
  texts: WriteReviewTexts;
  onSent: () => void;
}) {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(state => state.currentLocale);
  const authToken = useCustomerStore(state => state.me?.authToken);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const sent = useRef(false);

  const form = useForm<ReviewValues>({
    resolver: zodResolver(reviewSchema(formValidationMessages)),
    defaultValues: EMPTY_REVIEW,
  });

  const submit = form.handleSubmit(async values => {
    try {
      // The token is gone when the session ended while the modal was open.
      if (!authToken) throw new Error('Missing customer token');
      await createProductReview({ authToken, body: values, locale, slug });
    } catch (error) {
      const rest = applyServerErrors(error, form.setError, field =>
        isReviewField(field) ? field : undefined,
      );
      // Messages that belong to no field (and any other failure) are the error toast.
      if (rest) {
        toast({
          title: texts.error,
          description: rest.join('\n') || undefined,
          variant: 'destructive',
        });
      }
      return;
    }

    toast({ title: texts.success, variant: 'success' });
    sent.current = true;
    onSent();
    router.refresh();
    void queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCTS_REVIEWS] });
  });

  /** Call when the modal has closed: empties the form after a successful send. */
  const resetAfterSent = () => {
    if (!sent.current) return;
    sent.current = false;
    form.reset(EMPTY_REVIEW);
  };

  return { form, submit, resetAfterSent };
}
